import {
  Alert,
  AlertDescription,
  AlertIcon,
  Divider,
  Heading,
  HStack,
  Icon,
  Spinner,
  Stack,
  Text,
  Tooltip,
} from "@chakra-ui/react";
import { Trans, t } from "@lingui/macro";
import { currency } from "@src/utils/formatters";
import { isEmpty, map } from "lodash";
import { observer } from "mobx-react-lite";
import { InvoiceTypeEnum, useInvoiceStore } from "../../../../store";

export const SummaryBoxes = observer(function SummaryBoxes() {
  const store = useInvoiceStore();

  const actualSplit = (mode: "+" | "-", amount: number) => {
    if (amount === 0) return null;

    let label: string;

    if (mode === "-") {
      if (amount < 0) {
        label = t`left`;
      } else {
        label = t`over`;
      }
    } else {
      if (amount < 0) {
        label = t`over`;
      } else {
        label = t`left`;
      }
    }

    return `${label}: ${store.convertAndFormat(Math.abs(amount))}`;
  };

  return (
    <Stack p="2" pt="0" spacing="2">
      {store.showExchangeRateUnsetWarning && (
        <Alert
          colorScheme="orange"
          rounded="lg"
          status="warning"
          variant="subtle"
        >
          <AlertIcon />
          <AlertDescription>
            <Trans>Conversion rate for selected currency is not set.</Trans>
          </AlertDescription>
        </Alert>
      )}

      <Stack p="4" bg="#f4f6f8" rounded="lg" spacing="3">
        <HStack align="start" spacing="3">
          <Stack flex="1">
            <Heading color="grey.600" fontWeight="medium" size="2xs">
              {store.type === InvoiceTypeEnum.CreditNote
                ? t`Sum on credit note`
                : t`Sum on invoice`}
            </Heading>

            <Text fontSize="lg" fontWeight="bold">
              {store.convertAndFormat(store.amountForSplits)}
            </Text>
          </Stack>

          <Stack flex="1">
            <HStack>
              <Heading color="grey.600" fontWeight="medium" size="2xs">
                <Trans>Selected budget items</Trans>
              </Heading>

              {store.convertAmountFetcher.isLoading.value && (
                <Spinner size="sm" />
              )}
            </HStack>

            <Stack spacing="0">
              <Text fontSize="lg" fontWeight="bold">
                {store.convertAndFormat(
                  store.isTimeAndMaterialsSelected
                    ? store.timeAndMaterialsStore.totalToInvoice
                    : store.sumBudgetsSplits,
                )}
              </Text>

              {store.sumActualSplits !== 0 && (
                <Text color="red.500" fontSize="xs">
                  {store.type === InvoiceTypeEnum.CreditNote
                    ? actualSplit("-", store.sumActualSplits)
                    : actualSplit("+", store.sumActualSplits)}
                </Text>
              )}
            </Stack>
          </Stack>
        </HStack>

        {store.needsExchangeRate && (
          <>
            <Divider />

            {store.showInvoiceExchangeRate && (
              <HStack justify="space-between" p="3" bg="gray.200" rounded="lg">
                <HStack>
                  <Text fontWeight="medium">
                    <Trans>In</Trans>
                    &nbsp;
                    {store.selectedLegalEntityCurrency?.code}
                    &nbsp;
                    <Trans>- invoice rate</Trans>
                  </Text>

                  <Tooltip
                    hasArrow
                    label={t`Converted using the Invoice exchange rate (see right).`}
                    placement="top"
                    shouldWrapChildren
                  >
                    <Icon boxSize="3" name="info" />
                  </Tooltip>
                </HStack>

                <Text fontWeight="bold">
                  {store.selectedLegalEntityCurrency
                    ? currency.formatByCurrency(
                        store.convertedAmountUsingInvoiceExchangeRate,
                        store.selectedLegalEntityCurrency,
                      )
                    : ""}
                </Text>
              </HStack>
            )}

            {store.showWorkspaceExchangeRate && (
              <HStack justify="space-between" p="3" bg="gray.200" rounded="lg">
                <HStack>
                  <Text fontWeight="medium">
                    <Trans>In</Trans>
                    &nbsp;
                    {store.appStore.workspaceStore.settings?.currency.code}
                    &nbsp;
                    <Trans>- for project</Trans>
                  </Text>

                  <Tooltip
                    hasArrow
                    label={t`Converted using the Project exchange rate (see right).`}
                    placement="top"
                    shouldWrapChildren
                  >
                    <Icon boxSize="3" name="info" />
                  </Tooltip>
                </HStack>

                <Text fontWeight="bold">
                  {currency.formatByWorkspace(store.amountForSplits)}
                </Text>
              </HStack>
            )}
          </>
        )}
      </Stack>

      <Stack p="4" bg="#f4f6f8" rounded="lg" spacing="4">
        <Stack>
          <Heading color="grey.600" fontWeight="medium" size="2xs">
            <Trans>Our work</Trans>
          </Heading>

          <Text fontSize="md" fontWeight="bold">
            {store.convertAndFormat(
              store.isTimeAndMaterialsSelected
                ? store.timeAndMaterialsStore.totalToInvoice
                : store.sumOurWorkBudgetsSplits,
            )}
          </Text>
        </Stack>

        {map(store.sumExpenseBudgetSplitsByCategory, (sum, name) => (
          <Stack key={name}>
            <Heading color="grey.600" fontWeight="medium" size="2xs">
              {name}
            </Heading>

            <Text fontSize="md" fontWeight="bold">
              {sum ? store.convertAndFormat(sum) : "-"}
            </Text>
          </Stack>
        ))}

        {!isEmpty(store.form.prepayment_splits.$) && (
          <Stack>
            <Heading color="grey.600" fontWeight="medium" size="2xs">
              <Trans>Prepayment</Trans>
            </Heading>

            <Text fontSize="md" fontWeight="bold">
              {store.convertAndFormat(store.sumPrepaymentSplits)}
            </Text>
          </Stack>
        )}
      </Stack>
    </Stack>
  );
});
